import moment from 'moment';
import { State } from '@meraki-internal/state';
import { ISO8601DateTimeString } from '@autopylot-internal/tiles-client';
import { MissionsState } from '../../missions/model/MissionsState';
import { EditableMissionState } from '../../mission/model/EditableMissionState';
import { WeatherService } from '../WeatherService';
import { IHourlyForecast } from '../model/IHourlyForecast';
import { getTimezone } from '../../support/helpers/format';
import { TrackingService } from '../../support/tracking/TrackingService';

export interface IHourlyWeatherChartPageViewModelProps {

    // the active day and time that the chart should be focused on
    activeTime: ISO8601DateTimeString;

    // the mission the chart is for (what weather do we load)
    mission: EditableMissionState;

    // what timezone is the mission in
    timezone: string; // eg America/New_York

    // all hourly weather relevant to the mission (eg 24 hours times 10 days)
    allWeather?: IHourlyForecast[];

    // the hourly weather for the day (from activeTime)
    activeDayWeather?: IHourlyForecast[];
}

// TODO: can I do this with a factory
export class HourlyWeatherChartPageViewModel extends State<IHourlyWeatherChartPageViewModelProps> {
    static inject = () => [MissionsState, WeatherService, TrackingService];
    constructor(private missionsState: MissionsState, private weatherService: WeatherService, private trackingService: TrackingService) {

        super({
            // these get set in init, and the lifecycle makes it impractical
            // for down stream methods to get called before that
            // so easier on our consumers if we mark that as not optinal, but need this hack here
            // to make ctor happy
            activeTime: undefined as any,
            timezone: undefined as any,
            mission: undefined as any
        });
    }

    init = async ({ missionId, activeTime }: { missionId: string, activeTime: ISO8601DateTimeString }) => {
        const mission = await this.missionsState.getMissionById(missionId);

        const timezone = getTimezone(mission.getLocation());
        this.setState({
            activeTime: moment(activeTime).toISOString(),
            mission,
            timezone,
            activeDayWeather: undefined,
            allWeather: undefined,
        });

        // NOTE: when we add support for web (and therefore url hacking)
        // we may want to add support for handling "it isn't ready yet"
        // but on app, this is super edge case b/c there is no way to get here if the weather
        // hasn't loaded yet. Other edge case is if the system is behind by 24 hours.
        this.get10DaysWeather();
    };

    refresh = () => {
        return this.get10DaysWeather();
    };

    private get10DaysWeather = async () => {
        const allWeather = await this.weatherService.getChartForecast(this.state.mission!);
        this.setState({ allWeather });

        this.setDaysWeather();
    };

    formatTime = (time: ISO8601DateTimeString, format: string): string => {
        return moment.tz(time, this.state.timezone).format(format);
    };

    private setDaysWeather = async () => {
        if (!this.state.allWeather) {
            throw new Error('you cannot call setDaysWeather until allWeather has been set');
        }
        const activeStartTime = moment.tz(this.state.activeTime, this.state.timezone!).startOf('day').toISOString();
        const activeEndTime = moment.tz(this.state.activeTime, this.state.timezone!).add(1, 'day').startOf('day').toISOString();

        const daysWeather = this.state.allWeather.filter(w => w.forecastStart >= activeStartTime && w.forecastStart <= activeEndTime);
        this.setState({ activeDayWeather: daysWeather });
    };

    setActiveDay = async (activeDay: moment.Moment): Promise<void> => {
        this.trackDayClicked(activeDay);

        const currentHour = moment.tz(this.state.activeTime, this.state.timezone).hour();

        this.setState({
            activeTime: activeDay.clone().hour(currentHour).startOf('hour').toISOString(),
            activeDayWeather: undefined
        });

        await this.setDaysWeather();
    };

    private trackDayClicked = (activeDay: moment.Moment) => {
        this.trackingService.track('Hourly Weather Chart | Day Changed', () => ({
            day: activeDay.format('YYYY-MM-DD'),
            ...this.getTrackingMeta(activeDay.toISOString())
        }));
    };

    private getTrackingMeta = (time: ISO8601DateTimeString) => {
        const missionStartDay = moment.tz(this.state.mission.getStartTime(), this.state.timezone).startOf('day');

        // if mission started today and it is today, then 0
        // if mission started at 9 PM, then 12PM, is 1 day away (not 3 hours rounded down to 0 days)
        // not handling mission end time, as we expect multi day missions are rare
        const daysAfterMissionStart = moment.duration(moment.tz(time, this.state.timezone).startOf('day')
            .diff(missionStartDay))
            .asDays();

        const daysAfterToday = moment.duration(moment.tz(time, this.state.timezone).startOf('day')
            .diff(moment.tz(this.state.timezone).startOf('day')))
            .asDays();

        return {
            daysAfterMissionStart,
            absoluteDaysAfterMissionStart: Math.abs(daysAfterMissionStart),
            daysAfterToday,
            absoluteDaysAfterToday: Math.abs(daysAfterToday),
        };
    };

    private trackTimeChanged = (activeTime: ISO8601DateTimeString) => {
        this.trackingService.trackWithDebounce('Hourly Weather Chart | Hour Changed', () => ({
            hour: moment.tz(activeTime, this.state.timezone).format('HH:mm'),
            ...this.getTrackingMeta(activeTime)
        }));
    };

    changeMetric = (metricCode: string) => {
        if (metricCode === 'temperature'){
            return;
        }

        this.trackingService.track('Hourly Weather Chart | Metric Changed', () => ({
            metricFrom: 'temperature',
            metricTo: metricCode
        }));
    };

    setActiveTime = (activeTime: ISO8601DateTimeString): void => {
        this.trackTimeChanged(activeTime);
        this.setState({ activeTime: moment(activeTime).toISOString() });
    };

    isDayActive = (day: moment.Moment): boolean => {
        const format = 'YYYY-MM-DD';
        return Boolean(this.state.activeTime) && moment.tz(this.state.activeTime, this.state.timezone).format(format) === day.format(format);
    };

    getChartDays = (): moment.Moment[] => {
        const chartDays: ISO8601DateTimeString[] = [];
        if (!this.state.allWeather) {
            return [];
        }
        for (const { forecastStart } of this.state.allWeather) {
            const day = moment.tz(forecastStart, this.state.timezone).startOf('day').toISOString();
            if (!chartDays.includes(day)) {
                chartDays.push(day);
            }
        }

        return chartDays.map(time => moment.tz(time, this.state.timezone));
    };

    getAllDataForActiveHour = (): IHourlyForecast | undefined => {
        if (!this.state.activeDayWeather) {
            return undefined;
        }
        return this.state.activeDayWeather!.find(w => moment(w.forecastStart).toISOString() === this.state.activeTime);
    };

    getActiveIndex = (): number => {
        if (!this.state.activeDayWeather) {
            return -1;
        }
        const activeHour = this.state.activeDayWeather!.find(w => moment(w.forecastStart).toISOString() === this.state.activeTime);
        if (!activeHour) {
            return -1;
        }
        return this.state.activeDayWeather!.indexOf(activeHour);
    };

}
